
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface GeneratedQuestion {
  id: string;
  text: string;
  options: Array<{
    id: string;
    text: string;
    isCorrect: boolean;
  }>;
  explanation?: string;
  isDuplicate?: boolean;
  selected: boolean;
  difficulty?: string;
  grade?: string;
  subject?: string;
  topic?: {
    title: string;
    content: string;
  } | null;
}

export type DifficultyLevel = "easy" | "medium" | "hard";

export const generateQuestions = async (
  grade: string,
  subjectId: string,
  categoryId: string,
  count: number,
  difficulty: DifficultyLevel,
  customPrompt?: string,
  generateWithTopic: boolean = true
): Promise<GeneratedQuestion[]> => {
  try {
    console.log(`Generating ${count} questions with difficulty ${difficulty}, topic-based: ${generateWithTopic}`);
    
    if (customPrompt) {
      console.log("Using custom prompt for question generation");
    }
    
    // Get user's country and state for location-based questions
    const { data: userData } = await supabase.auth.getUser();
    let countryId = null;
    let stateId = null;
    
    if (userData?.user) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('country_id, state_id')
        .eq('id', userData.user.id)
        .single();
        
      if (profileData) {
        countryId = profileData.country_id;
        stateId = profileData.state_id;
        console.log(`Generating questions for country ID: ${countryId}, state ID: ${stateId}`);
      }
    }
    
    const { data, error } = await supabase.functions.invoke("generate-questions", {
      body: {
        grade,
        subject_id: subjectId,
        category_id: categoryId,
        count,
        returnAllQuestions: true,
        difficulty,
        customPrompt,
        generateWithTopic, // Pass this flag to the edge function
        country_id: countryId,
        state_id: stateId
      }
    });

    if (error) {
      console.error("Supabase function error:", error);
      throw error;
    }

    if (!data || !data.questions) {
      console.error("No questions data returned:", data);
      throw new Error("No questions were returned from the generation function");
    }

    console.log(`Received ${data.questions.length} questions from the generation function`);
    
    // Process all questions regardless of topic presence
    // We'll accept both topic and non-topic questions based on what was requested
    const questions = data.questions.map(q => ({
      ...q,
      selected: q.isDuplicate === true ? false : true
    }));
    
    console.log(`Processed ${questions.length} questions. ${questions.filter(q => q.selected).length} are selected by default.`);
    
    return questions;
  } catch (error) {
    console.error("Error generating questions:", error);
    toast.error("Error", {
      description: "Failed to generate questions. Please try again."
    });
    throw error;
  }
};

export const saveQuestions = async (
  grade: string,
  subjectId: string,
  categoryId: string,
  questions: GeneratedQuestion[],
  difficulty: DifficultyLevel,
  topicId?: string
): Promise<number> => {
  try {
    console.log(`Saving ${questions.length} questions to the database`);
    
    // Get user's country and state for location-based questions
    const { data: userData } = await supabase.auth.getUser();
    let countryId = null;
    let stateId = null;
    
    if (userData?.user) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('country_id, state_id')
        .eq('id', userData.user.id)
        .single();
        
      if (profileData) {
        countryId = profileData.country_id;
        stateId = profileData.state_id;
        console.log(`Saving questions for country ID: ${countryId}, state ID: ${stateId}`);
      }
    }
    
    // No need to filter questions based on topic consistency - accept both types
    let savedTopicId = topicId;

    // If we have topic-based questions, save the topic first
    if (questions.length > 0 && questions[0].topic) {
      console.log("Saving topic information:", questions[0].topic);
      
      const { data: topicData, error: topicError } = await supabase
        .from('topics')
        .insert({
          title: questions[0].topic.title,
          content: questions[0].topic.content,
          grade: grade,
          subject_id: subjectId,
          category_id: categoryId,
          country_id: countryId,
          state_id: stateId
        })
        .select()
        .single();

      if (topicError) {
        console.error("Error saving topic:", topicError);
        throw topicError;
      }

      savedTopicId = topicData.id;
      console.log(`Successfully saved topic with ID: ${savedTopicId}`);
    }
    
    // Create a simplified array of questions with options to save
    const saveOnlyQuestions = questions.map(q => ({ 
      text: q.text, 
      explanation: q.explanation,
      difficulty: q.difficulty || difficulty,
      topic_id: q.topic ? savedTopicId : null, // Only set topic_id if it's a topic-based question
      country_id: countryId,  // Add country_id
      state_id: stateId,      // Add state_id
      options: q.options.map(opt => ({
        text: opt.text,
        is_correct: opt.isCorrect // Explicitly map isCorrect to is_correct for consistency
      }))
    }));
    
    // Log the first question for debugging
    console.log("Questions data being sent to save:", JSON.stringify(saveOnlyQuestions.slice(0, 1)));
    console.log("Sample options data:", JSON.stringify(saveOnlyQuestions[0]?.options || []));
    
    const { data, error } = await supabase.functions.invoke("generate-questions", {
      body: {
        grade,
        subject_id: subjectId,
        category_id: categoryId,
        saveOnlyQuestions: saveOnlyQuestions,
        difficulty,
        country_id: countryId,
        state_id: stateId
      }
    });

    if (error) {
      console.error("Supabase function error when saving:", error);
      throw error;
    }

    console.log(`Successfully saved ${data.length || 0} questions to the database`);
    return data.length || 0;
  } catch (error) {
    console.error("Error saving questions:", error);
    toast.error("Error", {
      description: "Failed to save questions. Please try again."
    });
    throw error;
  }
};
